import React from 'react';

import { type Extend } from '@nfctron/nexus-core';
import { useContextRequired } from '@nfctron/nexus-react';

import { type AnalyticsModule } from './analytics.module';
import { type TrackingModule } from './tracking.module';

/**
 * Tracking module context
 * @private
 * @type {React.Context<TrackingModuleContextProps | undefined>}
 */
const TrackingModuleContext = React.createContext<TrackingModuleContextProps | undefined>(undefined);

/**
 * Tracking module context props
 * @private
 */
type TrackingModuleContextProps = {
	trackingModule: TrackingModule;
	analyticsModule: AnalyticsModule;
};

/**
 * Tracking module provider
 * @type {React.Provider<TrackingModule | undefined>}
 * @export
 */
export const TrackingModuleProvider: React.FunctionComponent<TrackingModuleProviderProps> = ({ trackingModule, analyticsModule, children }) => {
	return (
		<React.Fragment>
			<TrackingModuleContext
				value={{
					trackingModule,
					analyticsModule,
				}}
			>
				{children}
			</TrackingModuleContext>
		</React.Fragment>
	);
};

/**
 * Tracking module provider props
 * @private
 */
type TrackingModuleProviderProps = Extend<
	TrackingModuleContextProps,
	{
		children: React.ReactNode;
	}
>;

/**
 * Hook to access tracking module context
 * @returns {NonNullable<TrackingModule | undefined>}
 */
export const useTracking = () => useContextRequired(TrackingModuleContext);
